'use client';

import { useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import BlurImage from '@/components/BlurImage';
import ArrowIcon from '@/components/ArrowIcon';
import siteTheme from '@/lib/theme';
import { parseMarkdown } from '@/lib/markdown';

const { colors, fonts } = siteTheme;

export interface AccordionPanel {
  label: string;
  subtitle?: string;
  body?: string;
  image: string;
  image_hover?: string;
  href: string;
}

interface AccordionPanelsProps {
  panels: AccordionPanel[];
  heading?: string;
  subtitle?: string;
  learn_more_label?: string;
  learn_more_href?: string;
  /** Height of the accordion on desktop. Default: clamp(40rem, 80vh, 90rem) */
  height?: string;
}

export default function AccordionPanels({
  panels,
  heading,
  subtitle,
  learn_more_label,
  learn_more_href,
  height = 'clamp(40rem, 80vh, 90rem)',
}: AccordionPanelsProps) {
  // Desktop accordion state
  const [active, setActive] = useState<number | null>(null);

  // Mobile carousel state
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setActiveIndex(Math.round(el.scrollLeft / el.offsetWidth));
  }, []);

  const scrollTo = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' });
  };

  const handleTap = (i: number) => {
    setActive((prev) => (prev === i ? null : i));
  };

  /* ── Shared header ── */
  const headerEl = (heading || subtitle || (learn_more_label && learn_more_href)) ? (
    <div style={{ maxWidth: '144rem', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '2rem' }}>
        <div>
          {heading && (
            <h2 className="section-title" style={{ fontSize: 'clamp(2.4rem, 3vw, 4rem)', color: colors.gold, letterSpacing: '0.06em', marginBottom: '0.8rem' }}>
              {heading}
            </h2>
          )}
          {subtitle && (
            <p className="section-body" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.8rem)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
              <span style={{ fontFamily: fonts.script, fontSize: '2.2em', lineHeight: 0.8, verticalAlign: 'baseline' }}>{subtitle.charAt(0)}</span>
              {subtitle.slice(1)}
            </p>
          )}
        </div>
        {learn_more_label && learn_more_href && (
          <Link href={learn_more_href} className="btn-luxury-text ap-learn-more" style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--gold-dark)', display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
            textDecoration: 'none', flexShrink: 0, opacity: 0.85,
          }}>
            {learn_more_label} <ArrowIcon />
          </Link>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        /* DESKTOP ACCORDION */
        .ap-outer {
          flex-direction: column;
          height: 100svh;
          min-height: 60rem;
        }
        .ap-wrap {
          display: flex;
          width: 100%;
          flex: 1 1 0;
          min-height: 0;
          overflow: hidden;
          background: #1c1710;
        }
        .ap-panel {
          position: relative;
          flex: 1 1 0;
          overflow: hidden;
          cursor: pointer;
          transition: flex 0.65s cubic-bezier(0.77, 0, 0.18, 1);
        }
        .ap-panel.is-active    { flex: 4 1 0; }
        .ap-panel.is-collapsed { flex: 0.35 1 0; }
        .ap-img {
          position: absolute; inset: 0;
          transition: transform 0.65s cubic-bezier(0.77, 0, 0.18, 1), filter 0.65s ease;
          will-change: transform;
        }
        .ap-panel:not(.is-active) .ap-img         { transform: scale(1.06); filter: brightness(0.75) saturate(0.75); }
        .ap-panel.is-active .ap-img               { transform: scale(1); }
        .ap-panel:not(.is-active):not(.is-collapsed) .ap-img { filter: brightness(0.75) saturate(0.75); }
        .ap-gradient {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgb(5 4 2 / 50%) 0%, rgb(5 4 2 / 15%) 40%, transparent 100%);
          pointer-events: none;
        }
        .ap-label {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-90deg);
          white-space: nowrap;
          font-family: var(--font-sans); font-size: 1.8rem; font-weight: 400;
          letter-spacing: 0.32em; text-transform: uppercase;
          color: rgba(255,255,255,0.75);
          transition: opacity 0.3s ease, letter-spacing 0.35s ease, color 0.3s ease; pointer-events: none;
        }
        .ap-panel.is-active .ap-label { opacity: 0; }
        .ap-panel:not(.is-active):hover .ap-label {
          color: rgba(255,255,255,1);
          letter-spacing: 0.44em;
        }
        .ap-panel:not(.is-active):hover .ap-img {
          filter: brightness(0.9) saturate(0.85) !important;
        }
        .ap-content {
          position: absolute; bottom: 0; left: 0; right: 0;
          padding: clamp(2.4rem,4vw,4rem) clamp(2.4rem,4vw,5rem);
          opacity: 0; transform: translateY(1.6rem);
          transition: opacity 0.4s ease 0.2s, transform 0.4s ease 0.2s;
          pointer-events: none;
        }
        .ap-panel.is-active .ap-content { opacity: 1; transform: translateY(0); pointer-events: auto; }
        .ap-content-eyebrow {
          display: block; font-family: var(--font-sans); font-size: 1.15rem; font-weight: 400;
          letter-spacing: 0.28em; text-transform: uppercase; color: var(--gold); margin-bottom: 1rem;
        }
        .ap-content-title {
          font-family: var(--font-serif); font-size: clamp(3rem,4vw,6rem); font-weight: 300;
          letter-spacing: 0.04em; color: #fff; line-height: 1; margin: 0 0 1.4rem;
        }
        .ap-content-body {
          font-family: var(--font-sans); font-size: clamp(1.4rem,1.5vw,1.6rem); font-weight: 300;
          line-height: 1.75; color: rgba(255,255,255,0.65);
          margin: 0 0 2.4rem; max-width: 44rem;
          display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
          white-space: pre-line;
        }
        .ap-content-cta {
          display: inline-flex; align-items: center; gap: 0.8rem;
          font-family: var(--font-sans); font-size: 1.3rem; font-weight: 400;
          letter-spacing: 0.18em; text-transform: uppercase;
          color: #fff; border: 1px solid rgba(255,255,255,0.4);
          padding: 1rem 2.4rem; min-height: 4.4rem; text-decoration: none;
          border-radius: 0.5rem;
          transition: background 0.25s, border-color 0.25s, box-shadow 0.25s;
        }
        .ap-content-cta:hover {
          background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.75);
          box-shadow: 0 0 18px rgba(255,255,255,0.18), 0 0 40px rgba(255,255,255,0.08);
        }
        .ap-content-cta svg { transition: transform 0.25s ease; display: inline-block; }
        .ap-content-cta:hover svg { transform: translateX(5px); }
        .ap-learn-more:hover { color: var(--gold) !important; opacity: 1 !important; }
        .ap-panel + .ap-panel::before {
          content: ''; position: absolute; inset: 0 auto 0 0;
          width: 1px; background: rgba(180,145,80,0.25); z-index: 2; pointer-events: none;
        }

        /* MOBILE CAROUSEL */
        .ap-mob-scroll::-webkit-scrollbar { display: none; }
      ` }} />

      {/* ══ DESKTOP: accordion (hidden on mobile) ══ */}
      <div className="ap-outer hidden md:flex">
        {headerEl && (
          <section style={{ backgroundColor: '#1c1710', padding: `clamp(4rem, 7vw, 8rem) clamp(2rem, 6vw, 8rem) clamp(2.5rem, 4vw, 4rem)`, flexShrink: 0 }}>
            {headerEl}
          </section>
        )}
        <div className="ap-wrap">
          {panels.map((panel, i) => {
            const isActive    = active === i;
            const isCollapsed = active !== null && active !== i;
            return (
              <div
                key={panel.label}
                className={`ap-panel${isActive ? ' is-active' : ''}${isCollapsed ? ' is-collapsed' : ''}`}
                onClick={() => handleTap(i)}
                role="button" aria-expanded={isActive} aria-label={panel.label} tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTap(i); } }}
              >
                <div className="ap-img">
                  <div className="ap-img-base">
                    <BlurImage src={panel.image} alt={panel.label} fill className="object-cover" sizes="33vw" priority={i === 0} />
                  </div>
                  {panel.image_hover && (
                    <div className="ap-img-hover">
                      <BlurImage src={panel.image_hover} alt={`${panel.label} hover`} fill className="object-cover" sizes="33vw" />
                    </div>
                  )}
                </div>
                <div className="ap-gradient" />
                <span className="ap-label">{panel.label}</span>
                <div className="ap-content">
                  {panel.subtitle && <span className="ap-content-eyebrow">{panel.subtitle}</span>}
                  <h3 className="ap-content-title">{panel.label}</h3>
                  {panel.body && <div className="ap-content-body" dangerouslySetInnerHTML={{ __html: parseMarkdown(panel.body) }} />}
                  <Link href={panel.href || '#'} className="ap-content-cta" onClick={(e) => e.stopPropagation()}>
                    Explore <ArrowIcon variant="short" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ MOBILE: swipe carousel (hidden on desktop) ══ */}
      <section className="md:hidden" style={{ backgroundColor: '#1c1710' }}>

        {/* Header */}
        {headerEl && (
          <div style={{ padding: 'clamp(5rem, 8vw, 7rem) clamp(2rem, 5vw, 4rem) 3rem' }}>
            {headerEl}
          </div>
        )}

        {/* Swipe carousel */}
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="ap-mob-scroll"
            style={{
              display: 'flex',
              overflowX: 'scroll',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              width: '100%',
            }}
          >
            {panels.map((panel, i) => (
              <div
                key={panel.label}
                style={{
                  flex: '0 0 100%', width: '100%', minWidth: '100%',
                  scrollSnapAlign: 'start',
                  padding: '0 clamp(2rem, 5vw, 4rem)',
                  boxSizing: 'border-box',
                }}
              >
                {/* Image with overlay + label */}
                <div style={{ position: 'relative', paddingBottom: '75%', overflow: 'hidden', backgroundColor: '#111' }}>
                  {/* No priority — desktop accordion already preloads panel 0
                      at sizes="33vw"; adding it here at sizes="100vw" generates
                      a different optimized URL that goes unused on desktop. */}
                  <BlurImage
                    src={panel.image} alt={panel.label} fill
                    className="object-cover"
                    sizes="100vw"
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(5,4,2,0.75) 0%, rgba(5,4,2,0.2) 50%, transparent 100%)' }} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p className="card-meta" style={{ fontSize: '1.6rem', letterSpacing: '0.22em', color: '#fff', textAlign: 'center' }}>
                      {panel.label}
                    </p>
                  </div>
                </div>

                {/* Below image: content + CTA */}
                <div style={{ padding: '2.4rem 0 3.5rem' }}>
                  {panel.subtitle && (
                    <p className="card-meta" style={{ color: colors.gold, marginBottom: '1rem' }}>
                      {panel.subtitle}
                    </p>
                  )}
                  {panel.body && (
                    <div className="section-body cms-rich-text" style={{ fontSize: '1.5rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }} dangerouslySetInnerHTML={{ __html: parseMarkdown(panel.body) }} />
                  )}
                  <Link href={panel.href || '#'} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
                    fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 400,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: '#fff', border: '1px solid rgba(255,255,255,0.35)',
                    padding: '1rem 2.4rem', textDecoration: 'none',
                  }}>
                    Explore <ArrowIcon variant="short" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', paddingBottom: '4rem' }}>
          {panels.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              style={{
                width: i === activeIndex ? '2rem' : '0.6rem',
                height: '0.3rem',
                backgroundColor: i === activeIndex ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                borderRadius: '2px',
                transition: 'all 0.3s ease',
                border: 'none', cursor: 'pointer', padding: 0,
              }}
            />
          ))}
        </div>
      </section>
    </>
  );
}
